import { useState, useEffect, useMemo } from 'react';
import {
  Container,
  Typography,
  TextField,
  Button,
  Box,
  Alert,
  CircularProgress,
  Grid,
  Fade,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  TableSortLabel
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import Info from './Info';
import LanguageSelector from './LanguageSelector';
import { useAdminLanguage } from './AdminI18n';
import useTableSort from './useTableSort';

function FuneralAdmin() {
  const { t } = useAdminLanguage();
  const [funerals, setFunerals] = useState([]);
  const [formData, setFormData] = useState({ nomExploitation: '' });
  const [isEditing, setIsEditing] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [language, setLanguage] = useState('fr');

  useEffect(() => {
    fetchFunerals();
  }, [language]);

  const fetchFunerals = async () => {
    try {
      const response = await fetch(`/api/funerals?lang=${language}`);
      const data = await response.json();
      setFunerals(data);
      setLoading(false);
    } catch (err) {
      setError('Erreur lors du chargement des pompes funèbres');
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async () => {
    try {
      if (!formData.nomExploitation.trim()) {
        setError('Le nom d\'exploitation est obligatoire');
        return;
      }

      setLoading(true);
      setError('');
      setSuccess('');

      if (isEditing) {
        await fetch(`/api/funerals/${editingId}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ ...formData, lang: language }),
        });
        setSuccess('Pompe funèbre mise à jour avec succès');
      } else {
        await fetch('/api/funerals', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ ...formData, lang: language }),
        });
        setSuccess('Pompe funèbre ajoutée avec succès');
      }

      setFormData({ nomExploitation: '' });
      setIsEditing(false);
      setEditingId(null);
      fetchFunerals();
    } catch (err) {
      setError('Erreur lors de l\'enregistrement de la pompe funèbre');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (funeral) => {
    setIsEditing(true);
    setEditingId(funeral.id);
    setFormData({ nomExploitation: funeral.nomExploitation });
  };

  const handleDelete = async (id) => {
    try {
      setLoading(true);
      await fetch(`/api/funerals/${id}?lang=${language}`, {
        method: 'DELETE',
      });
      setSuccess('Pompe funèbre supprimée avec succès');
      fetchFunerals();
    } catch (err) {
      setError('Erreur lors de la suppression de la pompe funèbre');
    } finally {
      setLoading(false);
    }
  };

  const columns = useMemo(() => ([
    { id: 'nomExploitation', label: t("Nom d'exploitation"), sortable: true },
  ]), [t]);

  const { order, orderBy, sortedRows, handleRequestSort } = useTableSort(funerals, {
    defaultOrderBy: 'nomExploitation',
  });

  const renderCellContent = (funeral, columnId) => funeral[columnId];

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Fade in timeout={500}>
        <Box>
          <Typography
            variant="h4"
            gutterBottom
            align="center"
            color="primary"
            sx={{ fontWeight: 'bold', mb: 4 }}
          >
            {t('Gestion des pompes funèbres')}
          </Typography>

          <Info type="funeral" />

          <LanguageSelector 
            language={language} 
            onLanguageChange={setLanguage} 
          />

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {t(error)}
            </Alert>
          )}

          {success && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {t(success)}
            </Alert>
          )}

          <Box sx={{ mb: 4 }}>
            <Typography variant="h6" gutterBottom>
              {t(isEditing ? 'Modifier la pompe funèbre' : 'Ajouter une pompe funèbre')}
            </Typography>

            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  name="nomExploitation"
                  label={t("Nom d'exploitation")}
                  autoComplete="off"
                  value={formData.nomExploitation}
                  onChange={handleInputChange}
                  variant="outlined"
                  required
                  disabled={loading}
                />
              </Grid>

              <Grid item xs={12}>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleSubmit}
                  disabled={loading}
                  startIcon={loading && <CircularProgress size={20} />}
                >
                  {loading ? t('Enregistrement...') : t(isEditing ? 'Modifier' : 'Ajouter')}
                </Button>
                {isEditing && (
                  <Button
                    variant="outlined"
                    color="secondary"
                    onClick={() => {
                      setIsEditing(false);
                      setEditingId(null);
                      setFormData({ nomExploitation: '' });
                    }}
                    sx={{ ml: 2 }}
                  >
                    Annuler
                  </Button>
                )}
              </Grid>
            </Grid>
          </Box>

          <Box>
            <Typography variant="h6" gutterBottom>
              {t('Liste des pompes funèbres')}
            </Typography>

            <TableContainer component={Paper}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    {columns.map((column) => (
                      <TableCell
                        key={column.id}
                        sortDirection={orderBy === column.id ? order : false}
                      >
                        {column.sortable ? (
                          <TableSortLabel
                            active={orderBy === column.id}
                            direction={orderBy === column.id ? order : 'asc'}
                            onClick={() => handleRequestSort(column.id)}
                          >
                            {column.label}
                          </TableSortLabel>
                        ) : (
                          column.label
                        )}
                      </TableCell>
                    ))}
                    <TableCell>{t('Actions')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sortedRows.map((funeral) => (
                    <TableRow key={funeral.id}>
                      {columns.map((column) => (
                        <TableCell key={`${funeral.id}-${column.id}`}>
                          {renderCellContent(funeral, column.id)}
                        </TableCell>
                      ))}
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'nowrap' }}>
                          <IconButton
                            size="small"
                            onClick={() => handleEdit(funeral)}
                            color="primary"
                            disabled={loading}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={() => handleDelete(funeral.id)}
                            color="error"
                            disabled={loading}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        </Box>
      </Fade>
    </Container>
  );
}

export default FuneralAdmin;
